import { useState } from "react";
import axios from "axios";
import {
  FileText, Plus, Trash2, ExternalLink, BellRing, CheckCircle2,
  AlarmClock, HeartPulse, Globe,
} from "lucide-react";
import { API } from "../../App";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** Phase 3 — Partner Engagement panels for the company detail page. */

const DOC_TYPES = ["Agreement", "Relocation Guide", "Marketing", "Pricing", "Other"];
const REMINDER_TYPES = [
  { value: "follow_up", label: "Follow-up" },
  { value: "renewal", label: "Renewal" },
  { value: "networking", label: "Networking" },
  { value: "relationship", label: "Relationship" },
];

const HEALTH_TONE = {
  Healthy: "bg-emerald-100 text-emerald-700",
  "Needs Attention": "bg-amber-100 text-amber-700",
  "At Risk": "bg-red-100 text-red-700",
};

// ── Scorecard ─────────────────────────────────────────────────────────────
export function ScorecardPanel({ scorecard }) {
  if (!scorecard) return null;
  const { health_score, health_status, breakdown, metrics } = scorecard;
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6" data-testid="partner-scorecard-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-[#DE6E4B]" />
          <h3 className="font-semibold text-slate-900">Relationship Scorecard</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-[#0A2342]" data-testid="scorecard-score">{health_score}<span className="text-sm text-slate-400 font-normal">/100</span></span>
          <Badge variant="outline" className={`${HEALTH_TONE[health_status] || "bg-slate-100 text-slate-700"} border-transparent`} data-testid="scorecard-health-badge">
            {health_status}
          </Badge>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
        {breakdown.map((b) => (
          <div key={b.component} className="flex items-center gap-3" data-testid={`scorecard-bar-${b.component.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="w-36 shrink-0 text-xs text-slate-500 uppercase tracking-wide">{b.component}</div>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${b.points / b.max >= 0.7 ? "bg-emerald-500" : b.points > 0 ? "bg-amber-400" : "bg-slate-200"}`}
                style={{ width: `${Math.max((b.points / b.max) * 100, 4)}%` }}
              />
            </div>
            <div className="w-14 text-right text-xs font-semibold text-slate-700">{b.points}/{b.max}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
        <span>Last touch: <b className="text-slate-700">{metrics.days_since_last_touch != null ? `${metrics.days_since_last_touch}d ago` : "never"}</b></span>
        <span>Activities (90d): <b className="text-slate-700">{metrics.activities_90d}</b></span>
        <span>Referrals (180d): <b className="text-slate-700">{metrics.referrals_180d}</b></span>
        <span>Overdue reminders: <b className={metrics.overdue_reminders > 0 ? "text-red-600" : "text-slate-700"}>{metrics.overdue_reminders}</b></span>
      </div>
    </div>
  );
}

// ── Document Center ───────────────────────────────────────────────────────
export function DocumentsSection({ companyId, documents, authHeader, onReload }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", doc_type: "Agreement", url: "", notes: "", scope: "company", visible_to_partner: true });

  const submit = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast({ title: "Title and URL are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/partners/documents`, {
        company_id: form.scope === "company" ? companyId : null,
        title: form.title,
        doc_type: form.doc_type,
        url: form.url,
        notes: form.notes || null,
        visible_to_partner: form.visible_to_partner,
      }, { headers: authHeader });
      toast({ title: "Document added" });
      setOpen(false);
      setForm({ title: "", doc_type: "Agreement", url: "", notes: "", scope: "company", visible_to_partner: true });
      onReload();
    } catch (err) {
      toast({ title: "Add failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/partners/documents/${id}`, { headers: authHeader });
      toast({ title: "Document removed" });
      onReload();
    } catch (err) {
      toast({ title: "Delete failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Document Center</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#DE6E4B] hover:bg-[#c85d3f]" data-testid="partner-add-document-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="partner-document-dialog">
            <DialogHeader><DialogTitle>Add Document</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="partner-document-title" placeholder="Referral Agreement 2026" /></div>
              <div><Label>Link (URL) *</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="partner-document-url" placeholder="https://drive.google.com/…" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                    <SelectTrigger data-testid="partner-document-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Scope</Label>
                  <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                    <SelectTrigger data-testid="partner-document-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">This company only</SelectItem>
                      <SelectItem value="global">All partners (global)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.visible_to_partner}
                  onChange={(e) => setForm({ ...form, visible_to_partner: e.target.checked })}
                  data-testid="partner-document-visible"
                />
                Visible in the Partner Hub
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#DE6E4B] hover:bg-[#c85d3f]" onClick={submit} disabled={saving} data-testid="partner-document-submit">
                {saving ? "Saving…" : "Add Document"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {documents.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm">No documents yet — add agreements or relocation resources.</div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="partner-documents-list">
          {documents.map((d) => (
            <li key={d.id} className="p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`partner-document-row-${d.id}`}>
              <div className="min-w-0 flex items-start gap-3">
                <FileText className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {d.title}
                    {!d.company_id && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full"><Globe className="w-3 h-3" /> Global</span>
                    )}
                    {!d.visible_to_partner && <span className="ml-2 text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Internal only</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.doc_type}{d.notes ? ` · ${d.notes}` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                  Open <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600" onClick={() => remove(d.id)} data-testid={`partner-document-delete-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Reminder Engine ───────────────────────────────────────────────────────
export function RemindersSection({ companyId, contacts, reminders, authHeader, onReload }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ reminder_type: "follow_up", title: "", notes: "", due_date: "", contact_id: "" });

  const today = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    if (!form.title.trim() || !form.due_date) {
      toast({ title: "Title and due date are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = { company_id: companyId, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/partners/reminders`, payload, { headers: authHeader });
      toast({ title: "Reminder created" });
      setOpen(false);
      setForm({ reminder_type: "follow_up", title: "", notes: "", due_date: "", contact_id: "" });
      onReload();
    } catch (err) {
      toast({ title: "Create failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const markDone = async (id) => {
    try {
      await axios.put(`${API}/partners/reminders/${id}`, { status: "done" }, { headers: authHeader });
      toast({ title: "Reminder completed" });
      onReload();
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    }
  };

  const contactName = (cid) => {
    const c = contacts.find((x) => x.id === cid);
    return c ? `${c.first_name} ${c.last_name}` : null;
  };

  const typeLabel = (v) => REMINDER_TYPES.find((t) => t.value === v)?.label || v;

  const pending = reminders.filter((r) => r.status === "pending");
  const done = reminders.filter((r) => r.status !== "pending");

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Reminders</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#DE6E4B] hover:bg-[#c85d3f]" data-testid="partner-add-reminder-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Reminder
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="partner-reminder-dialog">
            <DialogHeader><DialogTitle>Add Reminder</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="partner-reminder-title" placeholder="Renew referral agreement" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.reminder_type} onValueChange={(v) => setForm({ ...form, reminder_type: v })}>
                    <SelectTrigger data-testid="partner-reminder-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{REMINDER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Due date *</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="partner-reminder-due" /></div>
              </div>
              <div>
                <Label>Contact (optional)</Label>
                <Select value={form.contact_id || "__none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "__none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None —</SelectItem>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#DE6E4B] hover:bg-[#c85d3f]" onClick={submit} disabled={saving} data-testid="partner-reminder-submit">
                {saving ? "Saving…" : "Create Reminder"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {reminders.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm">No reminders — schedule follow-ups, renewals, or networking touchpoints.</div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="partner-reminders-list">
          {[...pending, ...done].map((r) => {
            const overdue = r.status === "pending" && r.due_date < today;
            return (
              <li key={r.id} className={`p-4 flex items-center justify-between ${r.status !== "pending" ? "opacity-50" : ""}`} data-testid={`partner-reminder-row-${r.id}`}>
                <div className="min-w-0 flex items-start gap-3">
                  {overdue
                    ? <BellRing className="w-4 h-4 text-red-500 mt-1 shrink-0" />
                    : <AlarmClock className="w-4 h-4 text-slate-400 mt-1 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {r.title}
                      {r.auto_generated && <span className="ml-2 text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full">Auto</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <Badge variant="outline" className="mr-1 text-[10px] py-0">{typeLabel(r.reminder_type)}</Badge>
                      <span className={overdue ? "text-red-600 font-semibold" : ""}>Due {r.due_date}</span>
                      {contactName(r.contact_id) ? ` · ${contactName(r.contact_id)}` : ""}
                      {r.notes ? ` · ${r.notes}` : ""}
                    </div>
                  </div>
                </div>
                {r.status === "pending" ? (
                  <Button size="sm" variant="outline" onClick={() => markDone(r.id)} data-testid={`partner-reminder-done-${r.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Done
                  </Button>
                ) : (
                  <span className="text-xs text-emerald-600 font-medium shrink-0">Completed</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
